import React, { useEffect, useState, createContext, useContext } from 'react';
import { trackEvent } from '../services/AnalyticsService';
export interface UserData {
  name?: string;
  email?: string;
  birthDate?: string;
  birthTime?: string;
  birthLocation?: string;
  humanDesignType?: string;
  humanDesignDescription?: string;
  humanDesignAuthority?: string;
  humanDesignAuthorityDescription?: string;
  humanDesignDecisionGuidance?: string;
  values?: string[];
  valueDescriptions?: Record<string, string>;
  goal?: string;
  completedQuests?: string[];
  moodHistory?: Array<{
    mood: string;
    timestamp: number;
  }>;
  gardensRestored?: number;
  currentGarden?: string;
  lastLoginDate?: number;
  registrationDate?: number;
  writtenResponses?: Record<string, {
    content: string;
    timestamp: number;
    promptTitle: string;
    promptText: string;
  }>;
}
interface UserContextType {
  userData: UserData;
  updateUserData: (data: Partial<UserData>) => void;
  isOnboardingComplete: boolean;
  recordMood: (mood: string) => void;
  completeQuest: (questId: string) => void;
  resetUserData: () => void;
  login: (email: string) => Promise<boolean>;
  saveWrittenResponse: (activityId: string, content: string, promptTitle: string, promptText: string) => void;
  getWrittenResponse: (activityId: string) => {
    content: string;
    timestamp: number;
    promptTitle: string;
    promptText: string;
  } | undefined;
}
const defaultUserData: UserData = {
  gardensRestored: 0,
  currentGarden: 'Wisdom',
  completedQuests: [],
  moodHistory: [],
  writtenResponses: {}
};
const UserContext = createContext<UserContextType | undefined>(undefined);
export const UserProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [userData, setUserData] = useState<UserData>(defaultUserData);
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  useEffect(() => {
    const storedData = localStorage.getItem('sevenAfterUser');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setUserData(parsedData);
      setIsOnboardingComplete(true);
      // Track login event
      trackEvent('user_login', {
        userId: parsedData.email,
        humanDesignType: parsedData.humanDesignType,
        currentGarden: parsedData.currentGarden,
        gardensRestored: parsedData.gardensRestored
      });
    }
  }, []);
  const updateUserData = (data: Partial<UserData>) => {
    const updatedData = {
      ...userData,
      ...data
    };
    // If this is the first time setting an email, record registration date
    if (data.email && !userData.email) {
      updatedData.registrationDate = Date.now();
      // Track registration event
      trackEvent('user_registration', {
        userId: data.email,
        registrationDate: updatedData.registrationDate
      });
    }
    setUserData(updatedData);
    localStorage.setItem('sevenAfterUser', JSON.stringify(updatedData));
    // If we're updating with substantial user data, consider onboarding complete
    if (data.name || data.humanDesignType || data.goal) {
      setIsOnboardingComplete(true);
      // If onboarding is being completed, track that event
      if (data.goal) {
        trackEvent('onboarding_complete', {
          userId: updatedData.email,
          humanDesignType: updatedData.humanDesignType,
          values: updatedData.values,
          goal: data.goal
        });
      }
    }
  };
  const recordMood = (mood: string) => {
    const moodEntry = {
      mood,
      timestamp: Date.now()
    };
    const updatedMoodHistory = [...(userData.moodHistory || []), moodEntry];
    updateUserData({
      moodHistory: updatedMoodHistory
    });
    // Track mood check-in event
    trackEvent('mood_check_in', {
      userId: userData.email,
      mood: mood,
      timestamp: moodEntry.timestamp
    });
  };
  const completeQuest = (questId: string) => {
    const completedQuests = [...(userData.completedQuests || []), questId];
    // Every 3 quests completes a garden
    const gardensRestored = Math.floor(completedQuests.length / 3);
    // Garden values with their themed names
    const gardens = ['Wisdom', 'Love', 'Respect', 'Bravery', 'Honesty', 'Humility', 'Truth' // Eternal Compass
    ];
    const currentGarden = gardens[Math.min(gardensRestored, gardens.length - 1)];
    updateUserData({
      completedQuests,
      gardensRestored,
      currentGarden
    });
    // Track quest completion event
    trackEvent('quest_completed', {
      userId: userData.email,
      questId: questId,
      totalQuestsCompleted: completedQuests.length,
      gardensRestored: gardensRestored,
      currentGarden: currentGarden
    });
  };
  const login = async (email: string): Promise<boolean> => {
    // In a real app, this would make an API call to verify the user
    // For now, we'll just check if the email exists in localStorage
    const storedData = localStorage.getItem('sevenAfterUser');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (parsedData.email === email) {
        // Update last login date
        const updatedData = {
          ...parsedData,
          lastLoginDate: Date.now()
        };
        setUserData(updatedData);
        localStorage.setItem('sevenAfterUser', JSON.stringify(updatedData));
        setIsOnboardingComplete(true);
        // Track login event
        trackEvent('user_login', {
          userId: email,
          humanDesignType: parsedData.humanDesignType,
          currentGarden: parsedData.currentGarden,
          gardensRestored: parsedData.gardensRestored
        });
        return true;
      }
    }
    return false;
  };
  const resetUserData = () => {
    // Track account reset event
    if (userData.email) {
      trackEvent('account_reset', {
        userId: userData.email,
        gardensRestored: userData.gardensRestored,
        questsCompleted: userData.completedQuests?.length || 0
      });
    }
    setUserData(defaultUserData);
    localStorage.removeItem('sevenAfterUser');
    setIsOnboardingComplete(false);
  };
  // New function to save written responses
  const saveWrittenResponse = (activityId: string, content: string, promptTitle: string, promptText: string) => {
    const writtenResponses = {
      ...(userData.writtenResponses || {}),
      [activityId]: {
        content,
        timestamp: Date.now(),
        promptTitle,
        promptText
      }
    };
    updateUserData({
      writtenResponses
    });
    // Track written response saved
    trackEvent('written_response_saved', {
      userId: userData.email,
      activityId,
      contentLength: content.length,
      wordCount: content.trim().split(/\s+/).filter(Boolean).length
    });
  };
  // New function to get a written response
  const getWrittenResponse = (activityId: string) => {
    return userData.writtenResponses?.[activityId];
  };
  return <UserContext.Provider value={{
    userData,
    updateUserData,
    isOnboardingComplete,
    recordMood,
    completeQuest,
    resetUserData,
    login,
    saveWrittenResponse,
    getWrittenResponse
  }} data-id="element-820">
      {children}
    </UserContext.Provider>;
};
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};